import { getIntersectionRoot, getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const selector = '[data-sc-reveal="up"]';
const pendingClass = 'sc-reveal--pending';
const runningClass = 'sc-reveal--running';
const initialClass = 'sc-reveal--initial';
const seen = new WeakSet();
const pending = new Set();
const running = new Map();
let observer;
let root;
let frame;
let settleFrame;
let watchdog;
let generation = 0;
let failed = false;
let initialized = false;
let reducedMotion;
let initialPassPending = true;
let waitingForRestoration = false;
let restorationPending = false;
let scrollTarget;
let scrollStarted = false;

function onScroll() {
  if (scrollStarted || getScrollTop() <= 8) return;
  scrollStarted = true;
  scheduleRebuild();
}

function bindScroll() {
  const target = getScrollEventTarget();
  if (target === scrollTarget) return;
  scrollTarget?.removeEventListener('scroll', onScroll);
  scrollTarget = target;
  scrollTarget.addEventListener('scroll', onScroll, { passive: true });
}

function navigationType() {
  const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
  if (navigation) return navigation.type;
  const legacyType = window.performance?.navigation?.type;
  return legacyType === 0 ? 'navigate' : legacyType === 1 ? 'reload' : legacyType === 2 ? 'back_forward' : '';
}

function hasSavedScroll() {
  const savedTop = window.history?.state?.scrollTop;
  return Number.isFinite(savedTop) && savedTop > 2;
}

function mayRestorePosition() {
  const type = navigationType();
  return type === 'back_forward' || type === 'reload' || hasSavedScroll() || Boolean(window.location.hash);
}

function canPlayInitial() {
  const type = navigationType();
  return (type === 'navigate' || type === 'reload') && !hasSavedScroll() && !window.location.hash && getScrollTop() <= 2;
}

function cleanAnimation(element) {
  const animation = running.get(element);
  if (animation) {
    clearTimeout(animation.timer);
    element.removeEventListener('animationend', animation.end);
    element.removeEventListener('animationcancel', animation.end);
    running.delete(element);
  }
  element.classList.remove(runningClass, initialClass);
}

function reveal(element, animate = false, initial = false) {
  // A background tab must not consume an animation the visitor has not seen.
  if (animate && document.visibilityState === 'hidden') return;
  observer?.unobserve(element);
  pending.delete(element);
  element.classList.remove(pendingClass);
  const alreadySeen = seen.has(element);
  seen.add(element);
  cleanAnimation(element);
  if (!animate || alreadySeen) return;

  if (initial) element.classList.add(initialClass);
  element.classList.add(runningClass);
  const end = (event) => {
    if (event.target === element && event.animationName === 'sc-reveal-up') cleanAnimation(element);
  };
  const timer = setTimeout(() => cleanAnimation(element), 2200);
  running.set(element, { end, timer });
  element.addEventListener('animationend', end);
  element.addEventListener('animationcancel', end);
}

function showAll() {
  generation += 1;
  observer?.disconnect();
  clearTimeout(watchdog);
  for (const element of [...pending, ...running.keys(), ...document.querySelectorAll(selector)]) reveal(element);
}

function failOpen() {
  failed = true;
  cancelAnimationFrame(frame);
  cancelAnimationFrame(settleFrame);
  showAll();
}

// Keep failures local to the enhancement; never leave page content hidden.
function guard(callback) {
  return (...args) => {
    try { return callback(...args); }
    catch { failOpen(); }
  };
}

function bounds() {
  const height = document.documentElement.clientHeight || window.innerHeight;
  if (!root) return { top: 0, bottom: height };
  const rect = root.getBoundingClientRect();
  return { top: Math.max(0, rect.top), bottom: Math.min(height, rect.bottom) };
}

function revealRelated(target) {
  if (!(target instanceof Element)) return;
  for (const element of document.querySelectorAll(selector)) {
    if (element === target || element.contains(target) || target.contains(element)) reveal(element);
  }
}

function revealHash(hash = window.location.hash) {
  if (!hash || hash === '#') return;
  let id;
  try { id = decodeURIComponent(hash.slice(1)); }
  catch { return; }
  revealRelated(document.getElementById(id) || document.getElementsByName(id)[0]);
}

function rebuild() {
  frame = undefined;
  settleFrame = undefined;
  // Do not hide content before Horizon/browser restoration has positioned it.
  if (waitingForRestoration || document.visibilityState === 'hidden') return;
  const currentGeneration = ++generation;
  observer?.disconnect();
  clearTimeout(watchdog);
  // Remove preparations before measuring, so the offset does not change geometry.
  const wasPending = new Set(pending);
  for (const element of pending) element.classList.remove(pendingClass);
  pending.clear();
  // A pageshow/resize rebuild must not cut short an entry animation in progress.
  for (const element of running.keys()) {
    if (!element.isConnected) cleanAnimation(element);
  }
  if (failed || reducedMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
    showAll();
    return;
  }

  root = getIntersectionRoot();
  bindScroll();
  scrollStarted ||= getScrollTop() > 8;
  const viewport = bounds();
  if (viewport.bottom <= viewport.top) {
    showAll();
    return;
  }
  const bottomInset = Math.min(96, Math.round((viewport.bottom - viewport.top) * 0.15));
  observer = new IntersectionObserver(guard((entries) => {
    if (currentGeneration !== generation) return;
    clearTimeout(watchdog);
    const viewport = bounds();
    for (const entry of entries) {
      if (!pending.has(entry.target)) continue;
      if (!entry.target.isConnected) reveal(entry.target);
      else if (entry.isIntersecting) {
        if (entry.target.dataset.scRevealOnScroll === 'true' && !scrollStarted) continue;
        reveal(entry.target, true);
      }
      else if (entry.boundingClientRect.bottom <= viewport.top) reveal(entry.target);
    }
  }), { root, threshold: 0, rootMargin: `0px 0px -${bottomInset}px 0px` });

  const playInitial = initialPassPending && canPlayInitial();
  const restoring = restorationPending || (initialPassPending && !playInitial);
  initialPassPending = false;
  restorationPending = false;
  // Native hash navigation and restored positions are already visible states.
  revealHash();
  if (document.activeElement && document.activeElement !== document.body) revealRelated(document.activeElement);
  for (const element of document.querySelectorAll(selector)) {
    if (seen.has(element)) continue;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) continue;
    // Do not animate nested marked parents or elements outside the scroll root.
    if (element.querySelector(selector) || (root && !root.contains(element))) {
      reveal(element);
      continue;
    }
    if (rect.bottom <= viewport.top || (restoring && rect.top < viewport.bottom)) {
      reveal(element);
      continue;
    }
    if (playInitial && element.dataset.scRevealInitial === 'true' && rect.top < viewport.bottom - bottomInset) {
      reveal(element, true, true);
      continue;
    }
    // Previously prepared blocks stay prepared across load/resize rebuilds.
    // Fresh content enters through the same observer, including the lineup.
    if (!wasPending.has(element) && !playInitial && element.dataset.scRevealOnScroll !== 'true' && rect.top < viewport.bottom - bottomInset) {
      reveal(element);
      continue;
    }
    // Observe successfully before applying any hidden state.
    observer.observe(element);
    pending.add(element);
    element.classList.add(pendingClass);
  }
  // A broken/polyfilled observer that never delivers its first batch fails open.
  if (pending.size) watchdog = setTimeout(guard(showAll), 1600);
}

function scheduleRebuild(settle = false) {
  cancelAnimationFrame(frame);
  cancelAnimationFrame(settleFrame);
  frame = requestAnimationFrame(guard(() => {
    if (settle) settleFrame = requestAnimationFrame(guard(rebuild));
    else rebuild();
  }));
}

function initialize() {
  if (initialized) return;
  initialized = true;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Restored pages need pageshow; fresh entry only needs the first painted frames.
  // Do not wait on slow analytics/media requests before showing the hero motion.
  waitingForRestoration = document.readyState !== 'complete' && mayRestorePosition();
  document.addEventListener('visibilitychange', guard(() => {
    if (document.visibilityState !== 'hidden') scheduleRebuild(true);
  }));
  reducedMotion.addEventListener('change', guard(() => {
    if (reducedMotion.matches) showAll();
    else scheduleRebuild();
  }));
  scrollContainerMediaQuery.addEventListener('change', guard(() => scheduleRebuild(true)));
  window.addEventListener('resize', guard(() => scheduleRebuild()));
  // Horizon restores desktop scroll in a pageshow/popstate rAF. Measure after it.
  window.addEventListener('pageshow', guard((event) => {
    waitingForRestoration = false;
    if (event.persisted || navigationType() === 'back_forward') {
      restorationPending = true;
      initialPassPending = false;
      for (const element of running.keys()) cleanAnimation(element);
    }
    scheduleRebuild(true);
  }));
  window.addEventListener('popstate', guard(() => {
    waitingForRestoration = false;
    restorationPending = true;
    initialPassPending = false;
    for (const element of running.keys()) cleanAnimation(element);
    scheduleRebuild(true);
  }));
  window.addEventListener('hashchange', guard(() => revealHash()));
  window.addEventListener('beforeprint', guard(showAll));
  document.addEventListener('focusin', guard((event) => revealRelated(event.target)), true);
  document.addEventListener('click', guard((event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search) revealHash(url.hash);
  }), true);
  document.addEventListener('shopify:section:load', guard(() => scheduleRebuild(true)));
  document.addEventListener('sc:reveal-refresh', guard(() => scheduleRebuild(true)));
  document.addEventListener('shopify:section:unload', guard((event) => {
    const section = event.target;
    for (const element of [...pending, ...running.keys()]) {
      if (element === section || section.contains(element)) reveal(element);
    }
    scheduleRebuild(true);
  }));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guard(() => scheduleRebuild(true)), { once: true });
  } else {
    scheduleRebuild(true);
  }
}

guard(initialize)();
